import type Phaser from "phaser";

let betterCrtFragment: string | null = null;
const crtByScene = new WeakMap<Phaser.Scene, CrtHandles>();

export interface CrtHandles {
    capture: Phaser.GameObjects.CaptureFrame;
    display: Phaser.GameObjects.Shader;
}

export function adaptBetterCrtGlsl(source: string): string {
    const match = source.match(/void\s+mainImage\s*\([^)]*\)\s*\{([\s\S]*)\}\s*$/m);
    if (!match) {
        throw new Error("bettercrt.glsl: could not find mainImage()");
    }

    let body = match[1].trim();
    body = body.replace(/\btexture\s*\(/g, "texture2D(");
    body = body.replace(/\bfragColor\b/g, "gl_FragColor");
    body = body.replace(
        /vec2 uv = fragCoord \/ iResolution\.xy;/,
        "vec2 uv = outTexCoord;",
    );

    const warp = source.match(/float\s+warp\s*=\s*([\d.]+)/)?.[1] ?? "0.25";
    const scan = source.match(/float\s+scan\s*=\s*([\d.]+)/)?.[1] ?? "0.50";

    return `
precision mediump float;

uniform sampler2D iChannel0;
uniform vec2 iResolution;

varying vec2 outTexCoord;

float warp = ${warp};
float scan = ${scan};

void main(void)
{
    vec2 fragCoord = vec2(outTexCoord.x * iResolution.x, outTexCoord.y * iResolution.y);
    ${body}
}
`;
}

export async function loadBetterCrtShader(
    url = "/assets/shaders/bettercrt.glsl",
): Promise<string> {
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`Failed to load CRT shader: ${response.status}`);
    }

    betterCrtFragment = adaptBetterCrtGlsl(await response.text());
    return betterCrtFragment;
}

export function applyBetterCrt(scene: Phaser.Scene): CrtHandles {
    if (!betterCrtFragment) {
        throw new Error("CRT shader not loaded. Call loadBetterCrtShader() first.");
    }

    const existing = crtByScene.get(scene);
    if (existing) {
        return existing;
    }

    const captureKey = `__crt_capture_${scene.scene.key}__`;

    scene.cameras.main.setForceComposite(true);

    const width = scene.scale.width;
    const height = scene.scale.height;

    const capture = scene.add.captureFrame(captureKey);
    capture.setScrollFactor(0, 0);
    capture.setDepth(99_999);

    const display = scene.add.shader(
        {
            name: `bettercrt-${scene.scene.key}`,
            shaderName: `bettercrt-${scene.scene.key}`,
            fragmentSource: betterCrtFragment,
            setupUniforms: (
                setUniform: (name: string, value: unknown) => void,
                drawingContext: Phaser.Renderer.WebGL.DrawingContext,
            ) => {
                setUniform("iResolution", [drawingContext.width, drawingContext.height]);
            },
        },
        width / 2,
        height / 2,
        width,
        height,
        [captureKey],
    );

    display.setScrollFactor(0, 0);
    display.setDepth(100_000);

    const handles: CrtHandles = { capture, display };
    crtByScene.set(scene, handles);

    scene.events.once("shutdown", () => {
        capture.destroy();
        display.destroy();
        crtByScene.delete(scene);
    });

    return handles;
}
